import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import {
    Col,
    Row,
    Card,
    Table,
    Button,
    Form,
    Modal,
    Spinner,
    Badge,
    InputGroup,
    FormControl
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import {
    Package,
    Store,
    Search,
    Filter,
    Edit,
    Trash2,
    Download,
    RefreshCw,
    ArrowLeft,
    Plus,
    X,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import makeAnimated from "react-select/animated";

const NewArrivals = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);

    const { access } = useSelector((state) => state.login);
    const [newArrivalsAccess, setNewArrivalsAccess] = useState([]);
    const animatedComponents = makeAnimated();

    // Main states
    const [loading, setLoading] = useState(false);
    const [outlets, setOutlets] = useState([]);
    const [products, setProducts] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [filteredNewArrivals, setFilteredNewArrivals] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'products'

    // Filter states
    const [statusFilter, setStatusFilter] = useState('ACTIVE'); // 'ACTIVE', 'INACTIVE', 'ALL'
    const [searchQuery, setSearchQuery] = useState('');

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductsForAdd, setSelectedProductsForAdd] = useState([]);
    const [sequenceNumber, setSequenceNumber] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);

    // Download state
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Filter for add product modal
    const [productSearch, setProductSearch] = useState('');
    const [selectedProductType, setSelectedProductType] = useState('ALL');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryList, setCategoryList] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [filteredProductOptions, setFilteredProductOptions] = useState([]);
    const accessToken = localStorage.getItem('authToken');

    // Custom styles for react-select
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            minWidth: "200px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da"
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d"
        })
    };

    // Get permissions
    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("NewArrivals:")
            );
            setNewArrivalsAccess(filteredAccess);
        }
    }, [access]);

    // Fetch all outlets
    const getOutlets = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                const outletsData = response?.data?.data?.outlets || [];
                const outletOptions = outletsData.map(outlet => ({
                    value: outlet._id,
                    label: outlet.name || outlet.outletName || 'Unknown Outlet',
                    status: outlet.status
                }));
                setOutlets(outletOptions);
            }
        } catch (error) {
            showError(error, 'Failed to fetch outlets');
        } finally {
            setLoading(false);
        }
    };

    // Fetch all products for add modal
    const getAllProducts = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("product", { headers });

            if (response?.data?.status === true) {
                const productsData = response?.data?.data?.products || [];
                setProducts(productsData);
                
                // Prepare product options for react-select
                const options = productsData.map(product => ({
                    value: product._id,
                    label: product.name,
                    foodType: product.foodType,
                    classType: product.classType,
                    status: product.status,
                    category: product.category
                }));
                setProductOptions(options);
                setFilteredProductOptions(options);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    // Fetch category list
    const getCategoryList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("masters/category/get", { headers });

            if (response?.data?.status === true) {
                const categoriesData = response?.data?.data?.categories || [];
                const categoryOptions = categoriesData.map(category => ({
                    value: category._id,
                    label: category.name || category.categoryName || 'Unknown Category'
                }));
                setCategoryList(categoryOptions);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Fetch new arrivals for selected outlet
    const getNewArrivals = async (outletId = null) => {
        const outletParam = outletId || selectedOutlet?.value;

        if (!outletParam) {
            toast.warning('Please select an outlet first', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(
                `outlet-wise-new-arrivals/admin?outlet=${outletParam}&orderType=PICKUP`,
                { headers }
            );

            if (response?.data?.status === true) {
                const data = response?.data?.data?.outletWiseNewArrivals || [];
                setNewArrivals(data);
                applyFilters(data, statusFilter, searchQuery);
            }
        } catch (error) {
            showError(error, 'Failed to fetch new arrivals');
        } finally {
            setLoading(false);
        }
    };

    // Apply filters
    const applyFilters = (arrivalsData, status, search) => {
        let filtered = [...arrivalsData];

        // Apply status filter
        if (status !== 'ALL') {
            filtered = filtered.filter(item => {
                const productStatus = item?.product?.status || 'ACTIVE';
                return productStatus === status;
            });
        }

        // Apply search filter
        if (search.trim() !== '') {
            const query = search.toLowerCase();
            filtered = filtered.filter(item => {
                const productName = item?.product?.name || '';
                const productDesc = item?.product?.description || '';
                return productName.toLowerCase().includes(query) ||
                    productDesc.toLowerCase().includes(query);
            });
        }

        // Sort by sequence number
        filtered.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

        setFilteredNewArrivals(filtered);
    };

    // Filter products for add modal
    const getFilteredProductOptions = (category) => {
        let filtered = [...productOptions];

        // Filter by search
        if (productSearch.trim() !== '') {
            const query = productSearch.toLowerCase();
            filtered = filtered.filter(option =>
                option.label.toLowerCase().includes(query)
            );
        }

        // Filter by type
        if (selectedProductType !== 'ALL') {
            filtered = filtered.filter(option =>
                option.classType === selectedProductType
            );
        }

        // Filter by category
        if (selectedCategory) {
            filtered = filtered.filter(option =>
                option.category === selectedCategory.value
            );
        }

        // Filter out already added products
        const existingProductIds = newArrivals.map(item => item.product?._id);
        filtered = filtered.filter(option => 
            !existingProductIds.includes(option.value)
        );

        return filtered;
    };

    // Handle status filter change
    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        applyFilters(newArrivals, status, searchQuery);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        applyFilters(newArrivals, statusFilter, value);
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
        applyFilters(newArrivals, statusFilter, '');
    };

    // Handle view products
    const handleViewProducts = (outlet) => {
        setSelectedOutlet({
            value: outlet.value,
            label: outlet.label,
            status: outlet.status
        });
        setViewMode('products');
        getNewArrivals(outlet.value);
    };

    // Handle back to list
    const handleBackToList = () => {
        setViewMode('list');
        setSelectedOutlet(null);
        setNewArrivals([]);
        setFilteredNewArrivals([]);
    };

    // Handle edit sequence
    const handleEditSequence = (product) => {
        setSelectedProduct(product);
        setSequenceNumber(product.sequenceNumber || '');
        setShowEditModal(true);
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        setEditLoading(true);

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const data = {
                outlet: selectedOutlet?.value,
                product: selectedProduct?.product?._id,
                sequenceNumber: parseFloat(sequenceNumber)
            };

            const response = await axios.put('outlet-wise-new-arrivals/sequence', data, { headers });

            if (response?.data?.status === true) {
                toast.success('Sequence updated successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                });
                setShowEditModal(false);
                getNewArrivals();
            }
        } catch (error) {
            showError(error, 'Failed to update sequence');
        } finally {
            setEditLoading(false);
        }
    };

    // Handle delete
    const handleDelete = (product) => {
        setSelectedProduct(product);
        setShowDeleteModal(true);
    };

    const handleSubmitDelete = async () => {
        setDeleteLoading(true);

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const data = {
                _id: selectedProduct._id
            };

            const response = await axios.delete('outlet-wise-new-arrivals', { data, headers });

            if (response?.data?.status === true) {
                toast.success('Product removed from new arrivals!', {
                    position: "top-right",
                    autoClose: 3000,
                });
                setShowDeleteModal(false);
                getNewArrivals();
            }
        } catch (error) {
            showError(error, 'Failed to remove product');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Handle add products
    const handleAddProducts = () => {
        setShowAddModal(true);
        
    };

    useEffect(() => {
        getAllProducts();
        getCategoryList();
    }, []);

    // Handle product selection from react-select
    const handleProductSelect = (selectedOptions) => {
        const selectedProducts = selectedOptions || [];
        
        const mappedProducts = selectedProducts.map((option, index) => ({
            _id: option.value,
            name: option.label,
            sequenceNumber: selectedProductsForAdd.length + index + 1
        }));

        setSelectedProductsForAdd(prev => {
            // Merge new selections with existing ones, avoiding duplicates
            const existingIds = new Set(prev.map(p => p._id));
            const newProducts = mappedProducts.filter(p => !existingIds.has(p._id));
            
            const merged = [...prev, ...newProducts];
            // Update sequence numbers
            return merged.map((product, idx) => ({
                ...product,
                sequenceNumber: idx + 1
            }));
        });
    };

    const handleUpdateSequence = (index, value) => {
        const updatedProducts = [...selectedProductsForAdd];
        updatedProducts[index].sequenceNumber = parseFloat(value) || 0;
        setSelectedProductsForAdd(updatedProducts);
    };

    const handleRemoveSelectedProduct = (index) => {
        const updatedProducts = selectedProductsForAdd.filter((_, i) => i !== index);
        // Update sequence numbers
        updatedProducts.forEach((product, idx) => {
            product.sequenceNumber = idx + 1;
        });
        setSelectedProductsForAdd(updatedProducts);
    };

    // Remove product from react-select
    const handleRemoveProductFromSelect = (productId) => {
        setSelectedProductsForAdd(prev => {
            const updated = prev.filter(p => p._id !== productId);
            // Update sequence numbers
            return updated.map((product, idx) => ({
                ...product,
                sequenceNumber: idx + 1
            }));
        });
    };

    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        if (selectedProductsForAdd.length === 0) {
            toast.warning('Please select at least one product', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setAddLoading(true);

        try {
            const headers = { 
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            };

            const data = {
                outlet: selectedOutlet?.value,
                productData: selectedProductsForAdd.map(product => ({
                    product: product._id,
                    sequenceNumber: product.sequenceNumber
                }))
            };

            const response = await axios.post('outlet-wise-new-arrivals', data, { headers });

            if (response?.data?.status === true) {
                toast.success('Products added to new arrivals successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                });
                setShowAddModal(false);
                setSelectedProductsForAdd([]);
                setProductSearch('');
                setSelectedProductType('ALL');
                setSelectedCategory(null);
                getNewArrivals();
            }
        } catch (error) {
            showError(error, 'Failed to add products');
        } finally {
            setAddLoading(false);
        }
    };

    // Download Excel
    const handleDownloadExcel = async () => {
        if (!selectedOutlet) {
            toast.warning('Please select an outlet first', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setDownloadLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(
                `outlet-wise-new-arrivals/admin?outlet=${selectedOutlet.value}&download=true`,
                {
                    headers,
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `New_Arrivals_${selectedOutlet.label}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Excel file downloaded successfully!', {
                position: "top-right",
                autoClose: 3000,
            });
        } catch (error) {
            showError(error, 'Failed to download file');
        } finally {
            setDownloadLoading(false);
        }
    };

    // Error handler
    const showError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.error || defaultMessage;
        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
        });
    };

    // Format price
    const formatPrice = (price) => {
        return price ? `₹${parseFloat(price).toFixed(2)}` : 'N/A';
    };

    // Format date
    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const variants = {
            ACTIVE: 'success',
            INACTIVE: 'danger'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    // Get food type badge
    const getFoodTypeBadge = (type) => {
        const variants = {
            VEG: 'success',
            'NON_VEG': 'danger',
            VEGAN: 'primary',
            'VEG/VEGAN': 'info'
        };
        return <Badge bg={variants[type] || 'secondary'}>{type}</Badge>;
    };

    // Calculate discount
    const getDiscount = (mrp, offer) => {
        if (!mrp || !offer) return null;
        const discount = ((mrp - offer) / mrp) * 100;
        return Math.round(discount);
    };

    // Clear all filters in add modal
    const clearAllFilters = () => {
        setProductSearch('');
        setSelectedProductType('ALL');
        setSelectedCategory(null);
    };

    const handleCategoryChange = (option) => {
        console.log("Event |:", option?.value);
        console.log("Product Options :", productOptions);
        if (option?.value) {
            setSelectedCategory(option?.value ? option : null);
            let filteredproducts = productOptions?.filter((product) => {
                return product?.category?._id === option?.value;
            });
            
            setFilteredProductOptions(filteredproducts);
        } else {
            setFilteredProductOptions(productOptions);
        }       
    };

    // Initialize
    useEffect(() => {
        getOutlets();
    }, []);

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app p-3 p-lg-4">
                <div className="container shadow-sm" style={{ backgroundColor: "white" }}>
                    {/* Header */}
                    <Row className="pt-3">
                        <Col>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center">
                                    <Package className="me-3" size={26} color="#0d6efd" />
                                    <h4 className="fw-semibold mb-0">NEW ARRIVALS MANAGEMENT</h4>
                                </div>
                                {viewMode === 'products' && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleBackToList}
                                        size="sm"
                                    >
                                        <ArrowLeft size={16} className="me-1" />
                                        Back to Outlets
                                    </Button>
                                )}
                                {viewMode === 'list' && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleBackToList}
                                        size="sm"
                                        className="invisible"
                                    >
                                        Back to Outlets
                                    </Button>
                                )}
                            </div>
                        </Col>
                        <hr />
                    </Row>

                    {viewMode === 'list' ? (
                        /* Outlets List View */
                        <>
                            {/* Filters Section */}
                            <Row className="mb-3">
                                <Col md={6} lg={4} className="mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <Filter size={18} className="me-2 text-muted" />
                                        <h6 className="mb-0">Status Filter</h6>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant={statusFilter === 'ACTIVE' ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('ACTIVE')}
                                            className="flex-fill"
                                        >
                                            Active
                                        </Button>
                                        <Button
                                            variant={statusFilter === 'INACTIVE' ? 'danger' : 'outline-danger'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('INACTIVE')}
                                            className="flex-fill"
                                        >
                                            Inactive
                                        </Button>
                                        <Button
                                            variant={statusFilter === 'ALL' ? 'secondary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('ALL')}
                                            className="flex-fill"
                                        >
                                            All
                                        </Button>
                                    </div>
                                </Col>
                                <Col md={6} lg={8} className="mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <Search size={18} className="me-2 text-muted" />
                                        <h6 className="mb-0">Search Outlet</h6>
                                    </div>
                                    <InputGroup>
                                        <FormControl
                                            placeholder="Search by outlet name..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                        />
                                        {searchQuery && (
                                            <Button variant="outline-secondary" onClick={clearSearch}>
                                                Clear
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                            </Row>

                            {/* Outlets List */}
                            <Row className="mb-4">
                                <Col>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">OUTLETS</h5>
                                        <Button
                                            variant="outline-primary"
                                            onClick={getOutlets}
                                            disabled={loading}
                                            className="w-25"
                                            size="sm"
                                        >
                                            <RefreshCw size={16} className="me-2" />
                                            {loading ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>

                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 mb-0">Loading outlets...</p>
                                        </div>
                                    ) : outlets.length > 0 ? (
                                        <Row>
                                            {outlets
                                                .filter(outlet =>
                                                    statusFilter === 'ALL' || outlet.status === statusFilter
                                                )
                                                .filter(outlet =>
                                                    !searchQuery ||
                                                    outlet.label.toLowerCase().includes(searchQuery.toLowerCase())
                                                )
                                                .map((outlet, index) => (
                                                    <Col key={outlet.value} md={6} lg={4} className="mb-3">
                                                        <Card className="h-100 shadow-sm">
                                                            <Card.Body className="p-3">
                                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                                    <div>
                                                                        <div className="d-flex align-items-center mb-1">
                                                                            <Store size={18} className="text-muted me-2" />
                                                                            <h6 className="card-title mb-0 fw-semibold">
                                                                                {outlet.label}
                                                                            </h6>
                                                                        </div>
                                                                        <p className="text-muted mb-0 small">
                                                                            Click to view new arrivals
                                                                        </p>
                                                                    </div>
                                                                    <Badge bg={outlet.status === 'ACTIVE' ? 'success' : 'danger'}>
                                                                        {outlet.status}
                                                                    </Badge>
                                                                </div>

                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleViewProducts(outlet)}
                                                                    className="w-100"
                                                                >
                                                                    <Package size={16} className="me-1" />
                                                                    View New Arrivals
                                                                </Button>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                ))}
                                        </Row>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted mb-0">
                                                No outlets found.
                                            </p>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </>
                    ) : (
                        /* Products View */
                        <>
                            {/* Outlet Info */}
                            <Row className="mb-4">
                                <Col md={8}>
                                    <Card>
                                        <Card.Body>
                                            <div className="d-flex align-items-center">
                                                <Store size={24} className="text-primary me-3" />
                                                <div>
                                                    <h5 className="mb-1">Outlet: {selectedOutlet?.label}</h5>
                                                    <div className="d-flex align-items-center">
                                                        <Badge bg={selectedOutlet?.status === 'ACTIVE' ? 'success' : 'danger'}>
                                                            {selectedOutlet?.status || 'ACTIVE'}
                                                        </Badge>
                                                        <small className="text-muted ms-3">
                                                            Showing new arrivals for this outlet
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className={`${skin === 'dark' ? 'bg-dark' : 'bg-primary'} text-white`}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="mb-1">Total Products</h6>
                                                    <h3 className="mb-0">{filteredNewArrivals.length}</h3>
                                                </div>
                                                <Package size={36} />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Actions Section */}
                            <Row className="mb-4">
                                <Col md={8}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search size={18} />
                                        </InputGroup.Text>
                                        <FormControl
                                            placeholder="Search products by name"
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                        />
                                        {searchQuery && (
                                            <Button variant="outline-secondary" onClick={clearSearch}>
                                                Clear
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            onClick={handleAddProducts}
                                            className="w-50"
                                        >
                                            <Plus size={16} className="me-2" />
                                            Add Products
                                        </Button>
                                        <Button
                                            variant="outline-success"
                                            className="w-50"
                                            disabled={downloadLoading || filteredNewArrivals.length === 0}
                                            onClick={handleDownloadExcel}
                                        >
                                            <Download size={16} className="me-2" />
                                            {downloadLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Export"
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* Products Table */}
                            <Row>
                                <Col>
                                    <Card className='mb-3'>
                                        <Card.Header className="d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">New Arrivals - {selectedOutlet?.label}</h6>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => getNewArrivals()}
                                                disabled={loading}
                                            >
                                                <RefreshCw size={16} className="me-1" />
                                                {loading ? 'Refreshing...' : 'Refresh'}
                                            </Button>
                                        </Card.Header>
                                        <Card.Body>
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                    <p className="mt-2 mb-0">Loading products...</p>
                                                </div>
                                            ) : filteredNewArrivals.length > 0 ? (
                                                <div className="table-responsive">
                                                    <Table striped bordered hover>
                                                        <thead>
                                                            <tr>
                                                                <th width="60">#</th>
                                                                <th>Product Name</th>
                                                                <th>Type</th>
                                                                <th>Pricing</th>
                                                                <th>Status</th>
                                                                <th>Added On</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredNewArrivals.map((item, index) => {
                                                                const product = item.product || {};
                                                                const discount = getDiscount(product.mrp, product.offerPrice);

                                                                return (
                                                                    <tr key={item._id}>
                                                                        <td>
                                                                            <Badge bg="primary" className="fs-6">
                                                                                {item.sequenceNumber || index + 1}
                                                                            </Badge>
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-semibold">{product.name}</div>
                                                                            {product.description && (
                                                                                <small className="text-muted d-block">
                                                                                    {product.description.length > 50
                                                                                        ? `${product.description.substring(0, 50)}...`
                                                                                        : product.description}
                                                                                </small>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {getFoodTypeBadge(product.foodType)}
                                                                            <div className="mt-1">
                                                                                <small className="text-muted">
                                                                                    {product.classType}
                                                                                </small>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-semibold">
                                                                                {formatPrice(product.offerPrice || product.mrp)}
                                                                            </div>
                                                                            {/* {discount && (
                                                                                <div className="d-flex align-items-center mt-1">
                                                                                    <small className="text-decoration-line-through text-muted me-2">
                                                                                        {formatPrice(product.mrp)}
                                                                                    </small>
                                                                                    <Badge bg="danger">
                                                                                        {discount}% OFF
                                                                                    </Badge>
                                                                                </div>
                                                                            )} */}
                                                                        </td>
                                                                        <td>{getStatusBadge(product.status)}</td>
                                                                        <td>
                                                                            <small className="text-muted">
                                                                                {formatDate(item.createdAt)}
                                                                            </small>
                                                                        </td>
                                                                        <td>
                                                                            <div className="d-flex gap-2">
                                                                                <Button
                                                                                    variant="outline-primary"
                                                                                    size="sm"
                                                                                    onClick={() => handleEditSequence(item)}
                                                                                    title="Edit Sequence"
                                                                                >
                                                                                    <Edit size={14} />
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => handleDelete(item)}
                                                                                    title="Remove"
                                                                                >
                                                                                    <Trash2 size={14} />
                                                                                </Button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <Package size={48} className="text-muted mb-3" />
                                                    <h5>No New Arrivals Found</h5>
                                                    <p className="text-muted mb-3">
                                                        {newArrivals.length === 0
                                                            ? "No new arrivals found for this outlet."
                                                            : "No products match your search."}
                                                    </p>
                                                    <Button
                                                        variant="primary"
                                                        onClick={handleAddProducts}
                                                    >
                                                        <Plus size={16} className="me-2" />
                                                        Add Products
                                                    </Button>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>

                {/* Edit Sequence Modal */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <Edit size={20} className="me-2" />
                            Edit Sequence Number
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmitEdit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedProduct?.product?.name || ''}
                                    readOnly
                                    className="bg-light"
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Sequence Number</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={sequenceNumber}
                                    onChange={(e) => setSequenceNumber(e.target.value)}
                                    placeholder="Enter sequence number"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Lower numbers appear first. Use decimals for precise ordering.
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowEditModal(false)}
                                disabled={editLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={editLoading}
                            >
                                {editLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    'Update Sequence'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to remove this product from new arrivals?</p>
                        <div className="alert alert-warning">
                            <strong>Product:</strong> {selectedProduct?.product?.name}<br />
                            <strong>Outlet:</strong> {selectedOutlet?.label}
                        </div>
                        <p className="text-muted small">
                            This will only remove the product from new arrivals. The product will remain in your catalog.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleSubmitDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                "Delete Product"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Add Products Modal */}
                <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <Plus size={20} className="me-2" />
                            Add Products to New Arrivals
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmitAdd}>
                        <Modal.Body >
                            {/* Filters Section */}
                            <Row className="mb-1">
                                {/* <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Search Products</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <Search size={18} />
                                            </InputGroup.Text>
                                            <FormControl
                                                placeholder="Type to search..."
                                                value={productSearch}
                                                onChange={(e) => setProductSearch(e.target.value)}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Product Type</Form.Label>
                                        <Form.Select
                                            value={selectedProductType}
                                            onChange={(e) => setSelectedProductType(e.target.value)}
                                        >
                                            <option value="ALL">All Types</option>
                                            <option value="FOOD">Food</option>
                                            <option value="BEVERAGE">Beverage</option>
                                            <option value="COMBO">Combo</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col> */}
                                {/* <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Category</Form.Label>
                                        <Select
                                            options={categoryList}
                                            value={selectedCategory}
                                            // onChange={(e) => setSelectedCategory(e.target.value)}
                                            onChange={handleCategoryChange}
                                            components={animatedComponents}
                                            placeholder="Select category..."
                                            isClearable
                                            styles={customSelectStyles}
                                            
                                        />
                                    </Form.Group>
                                </Col> */}
                            </Row>

                            {/* Clear Filters Button */}
                            {/* {(productSearch || selectedProductType !== 'ALL' || selectedCategory) && (
                                <div className="mb-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearAllFilters}
                                    >
                                        Clear Filters
                                    </Button>
                                </div>
                            )} */}

                            {/* Product Selection */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Category</Form.Label>
                                        <Select
                                            options={categoryList}
                                            value={selectedCategory}
                                            // onChange={(e) => setSelectedCategory(e.target.value)}
                                            onChange={handleCategoryChange}
                                            components={animatedComponents}
                                            placeholder="Select category..."
                                            isClearable
                                            styles={customSelectStyles}
                                            
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">
                                            Select Products ({selectedProductsForAdd.length} selected)
                                        </Form.Label>
                                        <Select
                                            options={filteredProductOptions}
                                            value={[]} // Empty value to show placeholder
                                            onChange={handleProductSelect}
                                            placeholder="Search and select products..."
                                            isMulti
                                            isSearchable

                                            styles={customSelectStyles}
                                            closeMenuOnSelect={false}
                                            filterOption={(option, input) => {
                                                if (!input) return true;
                                                return option.label.toLowerCase().includes(input.toLowerCase());
                                            }}
                                            noOptionsMessage={({ inputValue }) =>
                                                inputValue
                                                    ? `No products found matching "${inputValue}"`
                                                    : "No products available"
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Products Table */}
                            {selectedProductsForAdd.length > 0 && (
                                <Card className="mb-4">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Selected Products ({selectedProductsForAdd.length})</h6>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => setSelectedProductsForAdd([])}
                                        >
                                            Clear All
                                        </Button>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="table-responsive">
                                            <Table striped hover size="sm">
                                                <thead>
                                                    <tr>
                                                        <th width="50">#</th>
                                                        <th>Product Name</th>
                                                        <th width="150">Sequence</th>
                                                        <th width="80">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedProductsForAdd.map((product, index) => (
                                                        <tr key={product._id}>
                                                            <td className="align-middle">
                                                                <Badge bg="primary">{index + 1}</Badge>
                                                            </td>
                                                            <td className="align-middle">
                                                                <div className="fw-semibold">{product.name}</div>
                                                            </td>
                                                            <td className="align-middle">
                                                                <Form.Control
                                                                    type="number"
                                                                    value={product.sequenceNumber}
                                                                    onChange={(e) => handleUpdateSequence(index, e.target.value)}
                                                                    min="0.01"
                                                                    step="0.01"
                                                                    size="sm"
                                                                />
                                                                <Form.Text className="text-muted small">
                                                                    Lower = First
                                                                </Form.Text>
                                                            </td>
                                                            <td className="align-middle">
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveSelectedProduct(index)}
                                                                    title="Remove"
                                                                >
                                                                    <X size={14} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Available Products Count */}
                            <div className="mb-3">
                                <small className="text-muted">
                                    {getFilteredProductOptions().length} products available for selection
                                    {selectedProductsForAdd.length > 0 && ` | ${selectedProductsForAdd.length} selected`}
                                </small>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setSelectedProductsForAdd([]);
                                    clearAllFilters();
                                }}
                                disabled={addLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={addLoading || selectedProductsForAdd.length === 0}
                            >
                                {addLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Adding...
                                    </>
                                ) : (
                                    `Add ${selectedProductsForAdd.length} Product${selectedProductsForAdd.length !== 1 ? 's' : ''}`
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default NewArrivals;